import { useEffect, useRef, useState } from 'react';

/**
 * 再生中に画面を消させない（Screen Wake Lock API）。
 *
 * スマホは放っておくと画面が消え、YouTube の埋め込みプレイヤーはその時点で
 * 再生を止める。YouTube 側が意図的にそうしているので埋め込みを使う限り
 * 回避できないが、そもそも画面を消させなければ止まらない。
 *
 * 限界:
 * - 画面が点いたままなのでバッテリーを食う
 * - 他のアプリに切り替えられると OS がロックを解放するため、そこまでは守れない
 * - ブラウザのタブを閉じられたら当然どうにもならない
 */
export function useWakeLock(active: boolean): { supported: boolean; held: boolean } {
  const [held, setHeld] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;

    const release = () => {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setHeld(false);
      if (sentinel) void sentinel.release().catch(() => {});
    };

    const acquire = async () => {
      // 表示中でないと取得できない。既に持っているなら取り直さない。
      if (!active || document.visibilityState !== 'visible' || sentinelRef.current) return;
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled || !active) {
          void sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        setHeld(true);
        sentinel.addEventListener('release', () => {
          sentinelRef.current = null;
          setHeld(false);
        });
      } catch {
        // 省電力モードなどで拒否されることがある。機能を落とすだけで進める。
        setHeld(false);
      }
    };

    if (active) void acquire();
    else release();

    // 画面を戻したときに取り直す。hidden になると OS が自動で解放するため。
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [active, supported]);

  return { supported, held };
}
